import * as React from 'react';
import * as _ from 'lodash';
import * as moment from 'moment';
import { ICprScoreResponse } from '@nkr/store/reports';
import { ICprScore } from '@nkr/models/index';
import { GridApi, GridReadyEvent, CsvExportParams, ProcessCellForExportParams, ColDef } from 'ag-grid-community';
import { Button, FormControl } from 'react-bootstrap';
import { IListItem } from '@nkr/models/index';
import Grid from './grid';

interface IProps {
    allCenters: boolean,
    title: string,
    showFcr: boolean,
    lookbackMonths: number,
    fileName: string,
    loadingMessage: string,
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getScores: (month: number, year: number) => Promise<ICprScoreResponse>
}

interface IState {
    data: ICprScore[],
    month: number,
    year: number,
    lastMonthEndDate: string,
    lastMonthStartDate: string,
    monthList: { name: string, monthNumber: number }[],
    yearList: IListItem[],
    gridApi: GridApi,
    columns: ColDef[],
}

export class ScoresReport extends React.Component<IProps, IState> {
    readonly state: IState = {
        data: [],
        lastMonthEndDate: '',
        lastMonthStartDate: '',
        month: 0,
        year: 0,
        monthList: [],
        yearList: [],
        gridApi: null,
        columns: []
    }

    onGridReady = (e: GridReadyEvent) => {
        this.setState({
            gridApi: e.api
        });
    }

    componentDidMount = () => {
        let { month, year, monthList, yearList } = this.populateLists();

        this.setState({
            month,
            year,
            monthList,
            yearList
        }, () => {
            this.getData();
        });
    }

    getData = () => {
        this.props.showLoading(this.props.loadingMessage).then(() => {
            this.props.getScores(this.state.month, this.state.year).then(response => {
                let { data, reportEndDate: lastMonthEndDate, reportStartDate: lastMonthStartDate } = response.data.results;

                this.setState({
                    data,
                    lastMonthEndDate,
                    lastMonthStartDate
                }, () => {
                    this.props.hideLoading();
                });
            });
        });
    }

    getKflCenterColumn = (value: number, startDate: string) => {
        let kflstart = moment(startDate);
        let quarterStart = moment(this.state.lastMonthStartDate);
        let quarterEnd = moment(this.state.lastMonthEndDate);

        if (value == -1) {
            return 'N/A';
        }

        if (kflstart >= quarterStart && kflstart < quarterEnd) {
            return 'New';
        }

        return value == 1 ? 'Yes' : 'No';
    }

    populateLists = () => {
        let lastMonth = moment().subtract(1, 'month');
        let months = moment.monthsShort().map((m, i) => ({ name: m, monthNumber: i + 1 }));

        let years = _.range(moment().year(), 2021, -1).map(year => {
            return {
                id: year,
                description: year.toString()
            };
        });

        return { month: lastMonth.month() + 1, year: lastMonth.year(), monthList: months, yearList: years };
    }

    handleMonthChange = (e: React.ChangeEvent<FormControl & HTMLSelectElement>) => {
        this.setState({
            month: parseInt(e.currentTarget.value)
        });
    };

    handleYearChange = (e: React.ChangeEvent<FormControl & HTMLSelectElement>) => {
        this.setState({
            year: parseInt(e.currentTarget.value)
        });
    };

    handleDateRangeSearch = () => {
        this.getData();
    }

    handleExport = () => {
        let params: CsvExportParams = {
            columnGroups: true,
            allColumns: true,
            fileName: this.props.fileName,
            processCellCallback: (params: ProcessCellForExportParams) => {
                let colDef = params.column.getColDef();

                if (colDef.colId == 'score')
                    return _.round((params.value) * 100, 1) + '%';

                if (colDef.colId == 'kflCenter') {
                    return this.getKflCenterColumn(params.value, params.node.data.kflCenterStart);
                }

                if (colDef.colId == 'kflCenterStart') {
                    let newValue = moment(params.value);
                    if (newValue.isValid()) {
                        return newValue.format('MM/DD/YYYY');
                    }
                    else {
                        return '';
                    }
                }

                return params.value;
            }
        };

        this.state.gridApi.exportDataAsCsv(params);
    }

    render = () => {
        return (
            <>
                {this.props.allCenters == false ?
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                        <div style={{ fontSize: 28, fontWeight: 500 }}>{this.props.title}</div>

                    </div>
                    :
                    <div></div>
                }

                <div className='well well-sm' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex' }}>
                        <div style={{ display: 'flex', alignItems: 'center', marginRight: 10 }}>
                            <div style={{ marginRight: 5 }}>Month:&nbsp;</div>
                            <div>
                                <FormControl
                                    componentClass='select'
                                    style={{ width: 'initial' }}
                                    value={this.state.month}
                                    onChange={this.handleMonthChange}
                                >
                                    {this.state.monthList.map(item =>
                                        <option key={'et-' + item.monthNumber} value={item.monthNumber}>{item.name}</option>
                                    )}
                                </FormControl>
                            </div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', marginLeft: 20 }}>
                            <div style={{ marginRight: 5 }}>Year:</div>
                            <div>
                                <FormControl
                                    componentClass='select'
                                    style={{ width: 'initial' }}
                                    value={this.state.year}
                                    onChange={this.handleYearChange}
                                >
                                    {this.state.yearList.map((item, index) =>
                                        <option key={'et-' + index} value={item.id}>{item.description}</option>
                                    )}
                                </FormControl>
                            </div>
                        </div>

                        <div style={{ marginLeft: 30 }}>
                            <Button bsStyle='primary' onClick={this.handleDateRangeSearch}>Search</Button>
                        </div>
                    </div>

                    <div>
                        <Button onClick={this.handleExport}>Export CSV</Button>
                    </div>
                </div>

                <Grid data={this.state.data} showFcr={this.props.showFcr} onGridReady={this.onGridReady} />

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>Using data from the last {this.props.lookbackMonths} months. Month ending {this.state.lastMonthEndDate}</div>
                </div>
            </>
        );
    }
}
